export function normalizedRole(userOrRole) {
  const raw = typeof userOrRole === "string" ? userOrRole : userOrRole?.role;
  const role = String(raw || "").trim().toLowerCase();
  if (role === "super_admin") return "superadmin";
  if (role === "admin") return "tenant_admin";
  if (role === "supervisor") return "manager";
  if (role === "callcenter") return "operator";
  return role;
}

export const ROLE_DEFAULT_PERMISSIONS = {
  superadmin: ["*"],
  tenant_admin: [
    "dashboard.view",
    "extensions.manage",
    "trunks.manage",
    "dids.manage",
    "routes.manage",
    "lcr.manage",
    "queues.manage",
    "ivr.manage",
    "moh.manage",
    "recordings.manage",
    "cdr.view",
    "reports.view",
    "users.manage",
    "agents.manage",
    "provisioning.manage",
    "antifraud.manage",
    "acl.manage",
    "tenant.settings",
  ],
  manager: [
    "dashboard.view",
    "extensions.view",
    "extensions.update",
    "queues.manage",
    "ivr.manage",
    "agents.manage",
    "cdr.view",
    "recordings.view",
    "reports.view",
  ],
  operator: [
    "dashboard.view",
    "agent.console",
    "queues.view",
    "cdr.view_limited",
    "calls.view",
  ],
  extension: [
    "webphone.use",
  ],
  viewer: [
    "dashboard.view",
    "extensions.view",
    "queues.view",
    "cdr.view_limited",
    "reports.view",
  ],
  billing: [
    "dashboard.view",
    "cdr.view",
    "billing.manage",
    "rates.manage",
    "financial_reports.view",
  ],
  support: [
    "dashboard.view",
    "diagnostics.view",
    "health.view",
    "extensions.view",
    "trunks.view",
    "routes.view",
    "cdr.view_limited",
  ],
};

export function getUserPermissions(user) {
  const role = normalizedRole(user);
  if (role === "superadmin") return ["*"];
  if (Array.isArray(user?.permissions)) return user.permissions;
  return ROLE_DEFAULT_PERMISSIONS[role] || [];
}

export function userHasPermission(user, permission) {
  const permissions = new Set(getUserPermissions(user));
  if (permissions.has("*") || permissions.has(permission)) return true;
  const moduleKey = String(permission || "").split(".", 1)[0];
  return permissions.has(`${moduleKey}.manage`) || permissions.has(`${moduleKey}.*`);
}

export function pathPermission(pathname) {
  const path = String(pathname || "").replace(/\/+$/, "") || "/";
  const routes = [
    { prefixes: ["/"], permission: "dashboard.view", exact: true },
    { prefixes: ["/clientes", "/clientes/cadastro", "/clientes/licencas"], permission: "tenant.settings" },
    { prefixes: ["/call-center/filas"], permission: "queues.manage" },
    { prefixes: ["/call-center/agentes"], permission: "agents.manage" },
    { prefixes: ["/call-center/mensagens-atendimento"], permission: "ivr.manage" },
    { prefixes: ["/call-center/mensagens-espera"], permission: "moh.manage" },
    { prefixes: ["/call-center/relatorios"], permission: "reports.view" },
    { prefixes: ["/supervisao"], permission: "reports.view" },
    { prefixes: ["/painel-tv"], permission: "reports.view" },
    { prefixes: ["/ramais/cadastro", "/ramais"], permission: "extensions.manage" },
    { prefixes: ["/ramais/agenda"], permission: "agenda.view" },
    { prefixes: ["/ramais/permissoes"], permission: "extensions.manage" },
    { prefixes: ["/ramais/pins-ligacao"], permission: "extensions.manage" },
    { prefixes: ["/ramais/monitorar"], permission: "extensions.view" },
    { prefixes: ["/ramais/grupo-captura"], permission: "extensions.manage" },
    { prefixes: ["/ramais/correio-voz"], permission: "voicemail.manage" },
    { prefixes: ["/ramais/grupos"], permission: "extensions.manage" },
    { prefixes: ["/troncos/sip-trunk", "/operadoras", "/troncos/cadastro"], permission: "trunks.manage" },
    { prefixes: ["/troncos/sip-trunk-clientes"], permission: "trunks.manage" },
    { prefixes: ["/troncos/dids"], permission: "dids.manage" },
    { prefixes: ["/troncos/rotas-entrada"], permission: "routes.manage" },
    { prefixes: ["/troncos/condicoes-tempo"], permission: "routes.manage" },
    { prefixes: ["/troncos/ura"], permission: "ivr.manage" },
    { prefixes: ["/relatorios/chamadas-ativas", "/live"], permission: "calls.view" },
    { prefixes: ["/relatorios/ligacoes", "/cdr"], permission: "cdr.view_limited" },
    { prefixes: ["/lcr/rotas-saida", "/lcr/regras-discagem", "/lcr/permissoes", "/lcr/condicoes-tempo", "/rotas"], permission: "lcr.manage" },
    { prefixes: ["/sistema/saude", "/health"], permission: "health.view" },
    { prefixes: ["/sistema/musica-espera", "/musica-espera"], permission: "moh.manage" },
    { prefixes: ["/sistema/anuncios", "/anuncios"], permission: "ivr.manage" },
    { prefixes: ["/sistema/freeswitch", "/freeswitch", "/sistema/registros", "/registros", "/sistema/configuracao-quebrada"], permission: "diagnostics.view" },
    { prefixes: ["/sistema/gravacoes"], permission: "recordings.view" },
    { prefixes: ["/sistema/backups", "/backups"], permission: "backups.manage" },
    { prefixes: ["/sistema/codigos-facilidade"], permission: "feature_codes.manage" },
    { prefixes: ["/sistema/seguranca-sip"], permission: "geoip.manage" },
    { prefixes: ["/sistema/geoip"], permission: "geoip.manage" },
    { prefixes: ["/administrativo/antifraude", "/antifraud"], permission: "antifraud.manage" },
    { prefixes: ["/acl"], permission: "acl.manage" },
    { prefixes: ["/administrativo/certificados", "/certificados"], permission: "tenant.settings" },
    { prefixes: ["/provisionamento/cisco", "/provisionamento/yealink", "/provisionamento-cisco"], permission: "provisioning.manage" },
    { prefixes: ["/auditoria", "/audit"], permission: "audit.view" },
    { prefixes: ["/usuarios"], permission: "users.manage" },
    { prefixes: ["/urls"], permission: "urls.view" },
    { prefixes: ["/console-agente"], permission: "agent.console" },
    { prefixes: ["/ramal-web"], permission: "webphone.use" },
  ];
  for (const route of routes) {
    const matched = route.exact
      ? route.prefixes.includes(path)
      : route.prefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
    if (matched) return route.permission;
  }
  return null;
}
